import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import { SELECTION_EVENT, trackUserEvent } from 'userActivityMiddleware';
import GiphyUpload from 'components/GiphyUpload';
import LoadingIndicator from 'components/LoadingIndicator';
import MessageForm from 'components/MessageForm';
import { MESSAGE_TYPE_IMAGE_CAPTION } from 'components/MessageForm';
import TextButton from 'components/TextButton';
import { getToken } from 'utils/auth';
import { getCredentialsSetting } from 'utils/cookies';
import { isAdmin } from 'utils/roles';
import { userShape } from 'utils/shapes';
import ModalCloseIcon from 'resources/icon-modal-close.svg';
import PoweredByGiphy from './PoweredByGiphy.png';
import './GiphyUploadModal.css';

class GiphyUploadModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      message: '',
      gifAltText: '',
      gifUrl: '',
      uploadRequested: false,
      uploadFailedError: null,
      showEmojiPicker: false,
      uploadRequiredError: false
    };
    this.onClose = this.onClose.bind(this);
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleMessageChange = this.handleMessageChange.bind(this);
    this.handleGiphyUrl = this.handleGiphyUrl.bind(this);
    this.handleGiphyUrlClear = this.handleGiphyUrlClear.bind(this);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.toggleEmojiPicker = this.toggleEmojiPicker.bind(this);
    this.addEmoji = this.addEmoji.bind(this);
  }

  handleInputChange(event) {
    this.setState({
      [event.target.name]: event.target.value,
      uploadRequiredError: false
    });
  }

  handleMessageChange(event) {
    this.setState({
      message: event.target.value,
      uploadRequiredError: false
    });
  }

  toggleEmojiPicker() {
    const isEmoji = this.state.showEmojiPicker;
    this.setState({
      showEmojiPicker: !isEmoji
    });
  }

  addEmoji(emoji) {
    const { message } = this.state;
    const text = `${message}${emoji.native}`;

    this.setState({
      message: text
    });
  }

  handleGiphyUrl(gifUrl) {
    this.setState({
      gifUrl,
      uploadFailedError: null,
      uploadRequiredError: false
    });
  }

  handleGiphyUrlClear() {
    this.setState({
      gifUrl: '',
      uploadRequiredError: false
    });
  }

  logUserEvent(eventValue) {
    trackUserEvent({
      activityType: SELECTION_EVENT,
      eventName: 'upload-gif',
      eventValue: eventValue,
      initiator: 'add-gif-submit-button'
    });
  }

  handleSubmit(event) {
    event.preventDefault();
    const { user, resourcePath } = this.props;
    const { gifUrl, gifAltText } = this.state;

    const valid = gifUrl && (!user || !isAdmin(user) || gifAltText.trim());
    if (valid) {
      this.setState({
        uploadRequested: true,
        uploadRequiredError: false
      });

      this.uploadGif(resourcePath);
    } else {
      this.setState({
        uploadRequiredError: true
      });
      this.logUserEvent('error');
    }
  }

  uploadGif(resourcePath) {
    const token = getToken();
    const { gifUrl } = this.state;

    var data = new FormData();
    data.append('Gif', gifUrl);
    fetch(process.env.REACT_APP_API_URI + resourcePath, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`
      },
      credentials: getCredentialsSetting(),
      body: data
    })
      .then(response => {
        if (response.status >= 200 && response.status < 300) {
          return response;
        } else {
          const error = new Error(response.statusText);
          error.response = response;
          throw error;
        }
      })
      .then(response => {
        return response.json();
      })
      .then(json => {
        const { message, gifAltText } = this.state;
        const { applyGiphy } = this.props;

        if (json.imageKey) {
          applyGiphy(
            json.imageKey || null,
            message.trim() || null,
            json.imageUrl || null,
            gifAltText.trim() || null
          );
          this.logUserEvent('success');
          this.onClose();
        }
      })
      .catch(error => {
        this.setState({
          uploadFailedError: error,
          uploadRequested: false
        });
        this.logUserEvent('failed');
      });
  }

  onClose() {
    const { onHide } = this.props;
    this.setState({
      message: '',
      gifAltText: '',
      gifUrl: '',
      uploadRequested: false,
      uploadFailedError: null,
      showEmojiPicker: false,
      uploadRequiredError: false
    });
    onHide();
  }

  renderGifAltTextInput(includeComment, uploadRequested) {
    const { user } = this.props;
    const { gifAltText } = this.state;
    const required = user && isAdmin(user);
    const validationState = required && !gifAltText.trim() ? 'error' : null;
    return (
      includeComment &&
      required && (
        <div className="image-alt-form">
          <FormGroup validationState={validationState}>
            <FormLabel>GIF alternative text{required ? '*' : ''}</FormLabel>
            <FormControl
              type="text"
              disabled={uploadRequested}
              name="gifAltText"
              value={gifAltText}
              onChange={this.handleInputChange}
              maxLength={4096}
              placeholder="Text for screen readers"
              aria-label="Text input for screen readers"
            />
          </FormGroup>
        </div>
      )
    );
  }

  render() {
    const {
      onHide,
      applyGiphy,
      includeComment,
      resourcePath,
      helpText,
      user,
      ...otherProps
    } = this.props;
    const {
      uploadRequested,
      uploadFailedError,
      showEmojiPicker,
      uploadRequiredError,
      gifUrl,
      gifAltText
    } = this.state;

    return (
      <Modal dialogClassName="giphy-upload-modal" {...otherProps}>
        <Modal.Header>
          <span className="giphy-label">
            <span className="giphy-label-text">Post a GIF</span>
            <img
              className="powered-by-giphy-logo"
              src={PoweredByGiphy}
              alt="Powered By Giphy"
            />
          </span>
          <div className="icon-modal-close-container">
            <TextButton className="modal-close-icon" onClick={this.onClose}>
              <img src={ModalCloseIcon} alt="Close Modal" />
            </TextButton>
          </div>
        </Modal.Header>
        <Modal.Body>
          {uploadFailedError && (
            <Alert variant="danger">
              {uploadFailedError.response &&
              uploadFailedError.response.status === 413
                ? 'The uploaded file is too large.'
                : 'The file upload failed.'}
            </Alert>
          )}
          {uploadRequiredError && (
            <Alert variant="danger">
              {!gifUrl && !gifAltText.trim()
                ? 'Please select a GIF and enter the GIF alternative text'
                : gifUrl && !gifAltText.trim()
                ? 'Please enter the GIF alternative text'
                : 'Please select a GIF'}
            </Alert>
          )}
          <div className="giphy-container">
            {uploadRequested && <LoadingIndicator overlay />}
            <GiphyUpload
              handleGiphyUrl={this.handleGiphyUrl}
              handleGiphyUrlClear={this.handleGiphyUrlClear}
            />
            {this.renderGifAltTextInput(includeComment, uploadRequested)}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <div className="feed-comment-container giphy-caption-form">
            <MessageForm
              onSubmit={this.handleSubmit}
              onChange={this.handleMessageChange}
              onEmojiPicker={this.toggleEmojiPicker}
              message={this.state.message}
              placeholder={MESSAGE_TYPE_IMAGE_CAPTION}
              disabled={uploadRequested}
              showEmojiPicker={showEmojiPicker}
              addEmoji={this.addEmoji}
            />
          </div>
        </Modal.Footer>
      </Modal>
    );
  }
}

GiphyUploadModal.propTypes = {
  onHide: PropTypes.func.isRequired,
  applyGiphy: PropTypes.func.isRequired,
  resourcePath: PropTypes.string.isRequired,
  includeComment: PropTypes.bool.isRequired,
  helpText: PropTypes.string,
  user: userShape,
  // Modal props
  closeButton: PropTypes.bool,
  show: PropTypes.bool
};

export default GiphyUploadModal;
